import { useState, useEffect } from 'react';

import { COURSE_STATUSES } from 'containers/user/learn/containers/content/cources/course.constants';
import {
  parseCmsNumericId,
} from 'containers/user/learn/containers/content/cources/courses.utils';

import api from 'api';
import { getCmsCourseMetaById } from 'api/generated/user-cms-metadata-controller/user-cms-metadata-controller';
import type { CmsCourseDto } from 'api/generated/schemas/cmsCourseDto';
import type {
  StrapiCourseAttributes,
  StrapiLessonAttributes,
  StrapiRecord,
} from 'api/strapi-api';

export interface EnrichedCourse {
  id: string | number;
  name?: string;
  coverDescription?: string;
  lessons?: { data: Array<StrapiRecord<StrapiLessonAttributes>> };
  content?: Array<{ id: string | number; type: string; [key: string]: unknown }>;
  averageProgress: number;
  status: string;
  route: string;
  coverImage: string | undefined;
  firstLessonId: string | number | undefined;
  [key: string]: unknown;
}

interface UseFetchCourseResult {
  loading: boolean;
  course: EnrichedCourse | null;
  courseMetadata: { data: CmsCourseDto } | null;
}

const useFetchCourse = (
  userId: string | undefined,
  courseId: string | number | undefined,
): UseFetchCourseResult => {
  const [loading, setLoading] = useState<boolean>(true);
  const [course, setCourse] = useState<EnrichedCourse | null>(null);
  const [courseMetadata, setCourseMetadata] = useState<{ data: CmsCourseDto } | null>(null);

  useEffect(() => {
    const fetchCourseDetails = async (): Promise<void> => {
      setLoading(true);
      let courseMetadata: CmsCourseDto | null = null;

      try {
        const courseRes = await api.getCourse(courseId as string | number, userId as string);

        const { id, attributes = {} as StrapiCourseAttributes } = courseRes.data.data;

        if (attributes.lessons) {
          attributes.lessons.data = attributes.lessons.data.filter(
            (lesson) => lesson.attributes.publishedAt
          );
        }

        let averageProgress = 0;
        let status: string = COURSE_STATUSES.UNKNOWN;

        try {
          const courseMetadataPayload = await getCmsCourseMetaById(
            userId as string,
            parseCmsNumericId(courseId as string | number),
          );

          const courseMetadataWrapper = { data: courseMetadataPayload };

          setCourseMetadata(courseMetadataWrapper);

          courseMetadata = courseMetadataPayload;

          if (courseMetadata.lessons?.length) {
            const courseLessonIds = new Set(
              attributes.lessons?.data?.map((lesson) => lesson.id)
            );

            const filteredLessons = courseMetadata.lessons.filter((lesson) => {
              const lessonMetaId = lesson.id;

              return lessonMetaId != null && courseLessonIds.has(lessonMetaId);
            });

            if (filteredLessons.length > 0) {
              const totalProgress = filteredLessons.reduce(
                (acc, lesson) => acc + (lesson.progress || 0),
                0
              );

              averageProgress = Math.round(
                totalProgress / (attributes.lessons?.data?.length ?? 1)
              );
            }
          }

          status =
            (courseMetadata.status &&
              COURSE_STATUSES[courseMetadata.status as keyof typeof COURSE_STATUSES]) ||
            COURSE_STATUSES.UNKNOWN;
        } catch (metadataError) {
          if ((metadataError as { response?: { status?: number } }).response?.status === 404) {
            console.error('Course metadata not found');
          }
        }

        const enrichedCourse: EnrichedCourse = {
          ...attributes,
          id,
          averageProgress,
          status: averageProgress === 100 ? COURSE_STATUSES.DONE : status,
          name: attributes.name,
          route: `/learn/course/${courseId}`,
          coverDescription: attributes.coverDescription,
          coverImage: attributes.coverImage?.url,
          firstLessonId: attributes.lessons?.data[0]?.id,
        };

        setCourse(enrichedCourse);
      } catch (error) {
        console.error('Failed to fetch course:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId && courseId) {
      fetchCourseDetails();
    }
  }, [userId, courseId]);

  return { loading, course, courseMetadata };
};

export default useFetchCourse;
